import { useEffect, useState } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw, Info, Lock, TrendingUp, Sparkles, Save, Check, Loader2 } from "lucide-react";
import { RubricEditor } from "@/components/RubricEditor";

function Bar({ label, pct, count }) {
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-0.5"><span className="font-medium">{label}</span><span className="font-mono text-zinc-500">{count} · {pct}%</span></div>
      <div className="h-2 bg-zinc-200"><div className="h-2 bg-klein" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-zinc-300 p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-head text-2xl font-extrabold mt-1">{value}</div>
      {sub && <div className="text-xs text-zinc-500 font-mono">{sub}</div>}
    </div>
  );
}

export default function ICP() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(60);
  const [directives, setDirectives] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = () => api.get("/icp").then((r) => { setData(r.data); setThreshold(r.data.settings?.prospect_threshold ?? 60); setDirectives(r.data.settings?.icp_directives || ""); });
  useEffect(() => { load(); }, []);

  const saveDirectives = async () => {
    try { await api.patch("/icp/settings", { icp_directives: directives }); toast.success("ICP guidance saved — now shaping all AI output"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const askAI = async () => {
    setAiBusy(true); setSuggestion(null);
    try { const { data } = await api.post("/icp/suggest", { directives }); setSuggestion(data); if (!data.ai) toast.error("AI couldn't generate a suggestion — try again"); }
    catch (e) { toast.error(errText(e)); } finally { setAiBusy(false); }
  };
  const applySuggestion = async () => {
    setApplying(true);
    try {
      await api.patch("/icp/settings", {
        weights: suggestion.weights,
        follower_sweet_low: suggestion.follower_sweet_low,
        follower_sweet_high: suggestion.follower_sweet_high,
      });
      toast.success("ICP updated & every lead re-scored");
      setSuggestion(null); load();
    } catch (e) { toast.error(errText(e)); } finally { setApplying(false); }
  };

  const saveThreshold = async () => {
    try { await api.patch("/icp/settings", { prospect_threshold: Number(threshold) }); toast.success("Prospecting threshold saved"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const recalc = async () => {
    try { const { data } = await api.post("/icp/recalc"); toast.success(data.changelog); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const toggleLock = async () => {
    const locked = data.settings.locked_fields || [];
    const next = locked.includes("follower_band") ? locked.filter((x) => x !== "follower_band") : [...locked, "follower_band"];
    try { await api.patch("/icp/settings", { locked_fields: next }); toast.success("Updated lock"); load(); } catch (e) { toast.error(errText(e)); }
  };

  if (!data) return <div className="p-6 font-mono text-sm">Loading ICP…</div>;
  const s = data.snapshot || {};
  const confColor = s.confidence === "low" ? "bg-red-50 border-red-300 text-red-800" : s.confidence === "medium" ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-emerald-50 border-emerald-300 text-emerald-800";
  const bandLocked = (data.settings.locked_fields || []).includes("follower_band");

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight">ICP Engine</h1>
          <p className="text-xs text-zinc-500 font-mono">Live Ideal Customer Profile · learned from won clients</p>
        </div>
        {user.role === "admin" && (
          <button data-testid="icp-recalc" onClick={recalc} className="flex items-center gap-2 bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium transition-colors">
            <RefreshCw className="h-4 w-4" /> Recalculate
          </button>
        )}
      </div>

      {/* Confidence */}
      <div data-testid="icp-confidence" className={`border-2 p-4 mb-5 flex items-center gap-3 ${confColor}`}>
        <Info className="h-5 w-5 shrink-0" />
        <div>
          <div className="font-bold">Based on {s.confidence_n || 0} won clients — <span className="uppercase">{s.confidence}</span> confidence</div>
          {s.confidence === "low" && <div className="text-xs">Under ~50 records. Treat these signals as directional, not definitive.</div>}
        </div>
      </div>

      {/* ICP Guidance — write what you feel, AI tunes the model */}
      {user.role === "admin" && (
        <div className="bg-white border-2 border-klein p-5 mb-5 rounded-xl" data-testid="icp-guidance-card">
          <h3 className="font-head font-bold mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-klein" /> ICP guidance — describe your ideal client</h3>
          <p className="text-xs text-zinc-500 mb-3">Write what makes a great client in your own words. This shapes the AI Composer & briefs, and you can ask the AI to translate it into updated scoring weights.</p>
          <textarea data-testid="icp-directives" value={directives} onChange={(e) => setDirectives(e.target.value)} rows={4}
            placeholder="e.g. Prioritise fintech & D2C founders who recently raised a seed round, 5k–25k followers. Avoid pure influencers and coaches. Verified founders are a plus."
            className="w-full border border-zinc-300 rounded-lg p-3 text-sm resize-none focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none" />
          <div className="flex items-center gap-2 mt-3">
            <button data-testid="save-directives" onClick={saveDirectives} className="flex items-center gap-2 border border-zinc-300 rounded-lg px-4 py-2 text-sm font-medium hover:border-klein hover:text-klein transition-colors">
              <Save className="h-4 w-4" /> Save guidance
            </button>
            <button data-testid="ask-ai-icp" onClick={askAI} disabled={aiBusy} className="flex items-center gap-2 bg-klein hover:bg-klein-hover text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60">
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Ask AI to suggest ICP
            </button>
          </div>

          {suggestion && (
            <div className="mt-4 border border-klein/40 bg-blue-50/60 rounded-xl p-4" data-testid="icp-suggestion">
              <div className="text-xs font-bold uppercase tracking-wider text-klein mb-2">AI suggestion</div>
              <p className="text-sm text-zinc-700 mb-3">{suggestion.rationale}</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                {Object.entries(suggestion.weights).map(([k, v]) => (
                  <div key={k} className="bg-white border border-zinc-200 rounded-lg px-2 py-1.5 text-center">
                    <div className="text-[10px] uppercase text-zinc-500 capitalize">{k}</div>
                    <div className="font-mono font-bold">{v}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-zinc-600 font-mono mb-3">Follower sweet-spot → {suggestion.follower_sweet_low.toLocaleString()} – {suggestion.follower_sweet_high.toLocaleString()}</div>
              <div className="flex items-center gap-2">
                <button data-testid="apply-suggestion" onClick={applySuggestion} disabled={applying || !suggestion.ai} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60">
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Apply & re-score all leads
                </button>
                <button data-testid="dismiss-suggestion" onClick={() => setSuggestion(null)} className="text-sm text-zinc-500 hover:text-zinc-900 px-3 py-2">Dismiss</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300 mb-5">
        <Stat label="Median experience" value={`${s.experience_median || 0} yrs`} sub={`±${s.experience_spread || 0} spread`} />
        <Stat label="Follower median" value={(s.follower_median || 0).toLocaleString()} sub={`${(s.follower_min || 0).toLocaleString()}–${(s.follower_max || 0).toLocaleString()}`} />
        <Stat label="Momentum vs Established" value={`${s.momentum_count || 0} / ${s.established_count || 0}`} sub="fresh vs seasoned" />
        <Stat label="Self-promo headlines" value={`${s.self_promo_pct || 0}%`} sub={`${s.verified_pct || 0}% verified`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Role distribution */}
        <div className="bg-white border border-zinc-300 p-5">
          <h3 className="font-head font-bold mb-3">Self-described title distribution</h3>
          {(s.role_distribution || []).slice(0, 8).map((r) => <Bar key={r.label} {...r} />)}
        </div>

        {/* Follower sweet spot */}
        <div className="bg-white border border-zinc-300 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head font-bold">Follower sweet-spot band</h3>
            {user.role === "admin" && (
              <button data-testid="lock-band" onClick={toggleLock} className={`flex items-center gap-1 text-xs px-2 py-1 border transition-colors ${bandLocked ? "bg-zinc-900 text-white border-black" : "border-zinc-400 hover:bg-zinc-100"}`}>
                <Lock className="h-3 w-3" /> {bandLocked ? "Locked" : "Lock band"}
              </button>
            )}
          </div>
          <div className="font-mono text-3xl font-extrabold text-klein">{(s.follower_sweet_low || 0).toLocaleString()} – {(s.follower_sweet_high || 0).toLocaleString()}</div>
          <p className="text-xs text-zinc-500 mt-2">Leads inside this band score the follower signal. Above 50k triggers a saturation warning.</p>
        </div>

        {/* Award keywords */}
        <div className="bg-white border border-zinc-300 p-5">
          <h3 className="font-head font-bold mb-3">Award / media keyword frequency</h3>
          <div className="flex flex-wrap gap-2">
            {(s.award_keyword_frequency || []).map((k) => (
              <span key={k.label} className="bg-zinc-900 text-white text-xs px-2 py-1 font-mono">{k.label} ×{k.count}</span>
            ))}
            {(!s.award_keyword_frequency || s.award_keyword_frequency.length === 0) && <span className="text-xs text-zinc-400">No award signals yet</span>}
          </div>
        </div>

        {/* Industry - display only */}
        <div className="bg-white border border-zinc-300 p-5">
          <h3 className="font-head font-bold mb-1">Industry breakdown</h3>
          <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold mb-3">Descriptive context only — never scored (industry is non-predictive)</p>
          {(s.industry_breakdown || []).slice(0, 6).map((r) => <Bar key={r.label} {...r} />)}
        </div>
      </div>

      {/* Scoring rubric editor (config-driven, editable without code) */}
      {user.role === "admin" && data.settings?.rubric && (
        <RubricEditor rubric={data.settings.rubric} onSaved={load} />
      )}

      {/* Prospecting threshold */}
      {user.role === "admin" && (
        <div className="bg-white border-2 border-black p-5 mt-5 rounded-xl" data-testid="prospect-threshold-card">
          <h3 className="font-head font-bold mb-1">Prospecting fit-score threshold</h3>
          <p className="text-xs text-zinc-500 mb-4">Only enriched prospects scoring at or above this reach a lead-gen rep's queue. Everything below is filed as "Low Fit" for your review.</p>
          <div className="flex items-center gap-4">
            <input type="range" min="0" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="flex-1 accent-klein" data-testid="threshold-range" />
            <input type="number" min="0" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-20 border border-zinc-400 rounded-lg px-2 py-1.5 text-sm font-mono text-center" data-testid="threshold-input" />
            <button data-testid="save-threshold" onClick={saveThreshold} className="bg-klein hover:bg-klein-hover text-white px-5 py-2 text-sm font-medium rounded-lg transition-colors">Save</button>
          </div>
        </div>
      )}

      {/* Changelog */}
      <div className="bg-white border border-zinc-300 p-5 mt-5">
        <h3 className="font-head font-bold mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> ICP changelog</h3>
        <div className="space-y-2">
          {(data.history || []).map((h, i) => (
            <div key={i} className="flex gap-3 text-sm border-l-2 border-klein pl-3">
              <span className="font-mono text-xs text-zinc-400 shrink-0">{h.date?.slice(0, 10)}</span>
              <span>{h.changelog}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
